import React, {useCallback, useEffect, useState} from "react";
import {useHistory, useParams} from "react-router-dom";
import useLoader from "../../../hooks/useLoader";
import {Button, Checkbox, CircularProgress, Grid, FormControl, InputLabel, Select, MenuItem, TextField} from "@mui/material";
import DashboardLayout from "../../../layouts/Dashboard"
import Paper from "@mui/material/Paper";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import {useDispatch} from "react-redux";
import LoadingButton from "@mui/lab/LoadingButton";
import {FORM_ATTRIBUTE_MAPPING_DETAIL_ROUTE, FORM_ATTRIBUTE_MAPPING_LIST_ROUTE} from "../../../routes";
import {formAttributeMappingCRUD, formAttributeCRUD} from "../../../http";
import { page, where, equals } from "../../../functions/resourceLinq1";
import Autocomplete from "@mui/material/Autocomplete";
import FormControlLabel from "@mui/material/FormControlLabel";

const FormAttributeMappingDetailForm = () => {
    const history = useHistory()
    const {id} = useParams();
    const [isNew,setIsNew] = useState(id==="new")
    const dispatch = useDispatch()
    const [fields, setFields] = useState({enabled: true})
    const {loading, start, stop} = useLoader(false)
    
    const [formAttributeTypes, setFormAttributeTypes] = useState([])
    const [formAttributes, setFormAttributes] = useState([])
    const [sourceNumber, setSourceNumber] = useState('')
    const [targetAttributes, setTargetAttributes] = useState([])
    const [targetNumberFilter, setTargetNumberFilter] = useState('')
    
    const targetTypes = ['result', 'protocol', 'objectReferral'];

    const loadTypes = useCallback(() => {
        // load all form attributes and extract unique types
        formAttributeCRUD
            .toList(page(null, 0, null))
            .then(list => {
                const types = Array.from(new Set((list || []).map(_ => _.type).filter(Boolean)))
                setFormAttributeTypes(types)
            })
            .catch(err => console.error(err))
    }, [])

    const loadMembersByType = useCallback((type) => {
        if (!type) {
            setFormAttributes([])
            return
        }
        formAttributeCRUD
            .toList(where(page(null, 0, null), { type: equals(type) }))
            .then(list => setFormAttributes(list || []))
            .catch(err => console.error(err))
    }, [])

    const fetch = useCallback(() => {
        start()
        if(!isNew){
            formAttributeMappingCRUD.get(id).then(data => {
                setFields(state=>{
                    return {
                        ...state,
                        sourceType: data.sourceType || '',
                        sourceMember: data.sourceMember || '',
                        targetType: data.targetType || '',
                        targetMember: data.targetMember || '',
                        targetNumber: data.targetNumber || '',
                        sourceMemberMap: data.sourceMemberMap ? data.sourceMemberMap.toString() : '',
                        enabled: (data.enabled !== undefined ? data.enabled : true)
                    }
                })
                setSourceNumber(data.sourceNumber ? String(data.sourceNumber) : '')
                // targetNumberFilter будет установлен автоматически в useEffect
                stop()
            })
        }else{
            stop()
        }
    }, [dispatch, id])

    useEffect(fetch, [fetch])

    useEffect(() => {
        loadTypes()
    }, [loadTypes])

    useEffect(() => {
        if (fields?.sourceType) {
            loadMembersByType(fields.sourceType)
        } else {
            setFormAttributes([])
        }
    }, [fields?.sourceType, loadMembersByType])
    
    useEffect(() => {
        if (fields && fields.targetType) {
            formAttributeCRUD
                .toList(where(page(null, 0, null), { type: equals(fields.targetType) }))
                .then(list => setTargetAttributes(list || []))
                .catch(err => console.error(err))
        } else {
            setTargetAttributes([])
        }
    }, [fields?.targetType])
    
    // Автоматически устанавливаем targetNumberFilter при загрузке данных
    useEffect(() => {
        if (!isNew && fields.targetMember && targetAttributes.length > 0 && !targetNumberFilter) {
            const memberAttr = targetAttributes.find(a => a && a.member === fields.targetMember)
            if (memberAttr && memberAttr.number) {
                setTargetNumberFilter(String(memberAttr.number))
                setFields(prev => ({...prev, targetNumber: String(memberAttr.number)}))
            }
        }
    }, [targetAttributes, fields.targetMember, isNew, targetNumberFilter])
    
    useEffect(() => {
        if (!fields.targetMember) return;
        if (!targetNumberFilter) return; // Если фильтр не установлен, не очищаем
        var filtered = (targetAttributes || []).filter(function(a){ return String(a && a.number) === String(targetNumberFilter) });
        var has = false;
        for (var i = 0; i < filtered.length; i++) {
            if (filtered[i] && filtered[i].member === fields.targetMember) { has = true; break; }
        }
        // Только очищаем если пользователь вручную изменил фильтр (не при первой загрузке)
        if (!has && filtered.length > 0) { 
            setFields(prev => ({...prev, targetMember: ''})); 
        }
    }, [targetNumberFilter, targetAttributes])
    
    const numberSet = new Set()
    const numberOptions = []
    ;(formAttributes || []).forEach(function(a){
        if (a && a.number !== undefined && a.number !== null && a.number !== '' && !numberSet.has(String(a.number))) {
            numberSet.add(String(a.number))
            numberOptions.push({ value: String(a.number), label: String(a.number) })
        }
    })

    const filteredAttributes = sourceNumber ? (formAttributes || []).filter(function(a){ return String(a && a.number) === String(sourceNumber) }) : (formAttributes || [])
    
    const seenMembers = new Set()
    const memberOptions = []
    ;(filteredAttributes || []).forEach(function(attribute) {
        if (attribute && attribute.member && !seenMembers.has(attribute.member)) {
            seenMembers.add(attribute.member)
            const desc = attribute.memberDescription || ''
            const typ = attribute.memberType || ''
            const label = (desc ? desc : '') + ' | ' + attribute.member + ' | ' + (typ ? typ : '')
            memberOptions.push({
                value: attribute.member,
                label: label,
                data: attribute
            })
        }
    })

    let selectedAttribute = null
    for (let i = 0; i < memberOptions.length; i++) {
        if (memberOptions[i].value === (fields.sourceMember || '')) {
            selectedAttribute = memberOptions[i].data
            break
        }
    }
    
    const targetNumberSet = new Set()
    const targetNumberOptions = []
    ;(targetAttributes || []).forEach(function(a){
        if (a && a.number !== undefined && a.number !== null && a.number !== '' && !targetNumberSet.has(String(a.number))) {
            targetNumberSet.add(String(a.number))
            targetNumberOptions.push({ value: String(a.number), label: String(a.number) })
        }
    })
    const filteredTargetAttributes = targetNumberFilter ? (targetAttributes || []).filter(function(a){ return String(a && a.number) === String(targetNumberFilter) }) : (targetAttributes || [])
    const targetMemberOptions = []
    const seenTargetMembers = new Set()
    ;(filteredTargetAttributes || []).forEach(function(attribute) {
        if (attribute && attribute.member && !seenTargetMembers.has(attribute.member)) {
            seenTargetMembers.add(attribute.member)
            const desc = attribute.memberDescription || ''
            const typ = attribute.memberType || ''
            const label = (desc ? desc : '') + ' | ' + attribute.member + ' | ' + (typ ? typ : '')
            targetMemberOptions.push({
                value: attribute.member,
                label: label,
                data: attribute
            })
        }
    })

    const submit = useCallback(() => {
        const submitData = {
            ...fields,
            sourceNumber: sourceNumber || undefined,
            sourceMemberMap: fields.sourceMemberMap ? eval(`(${fields.sourceMemberMap})`) : undefined
        };

        if (!isNew){
            formAttributeMappingCRUD.edit({
                id:id,
                ...submitData
            }).catch(err=>{
                alert(err)
            })
        } else {
            formAttributeMappingCRUD.create({
                ...submitData
            }).then(() => {
                setIsNew(false);
                history.push(FORM_ATTRIBUTE_MAPPING_LIST_ROUTE);
            }).catch((err) => {
                alert(err);
            });
        }
    }, [id, dispatch, fields, sourceNumber])

    const deleteForm = useCallback(()=>{
        formAttributeMappingCRUD.delete(id).then(()=>{
            history.push(FORM_ATTRIBUTE_MAPPING_LIST_ROUTE)
        }).catch(err=>{
            alert(err)
        })
    },[id])
    
    return (
        <DashboardLayout>
            {loading ? <CircularProgress/> : <>
                <Grid item xs={12}>
                    <Paper>
                        <Toolbar style={{display:"flex", justifyContent:"space-between"}}>
                            <Typography id="modal-modal-title" variant="h5" component="h2">
                                {isNew?"Создание":"Редактирование"} Маппинга форм<br/>
                            </Typography>
                            <div>
                                <Button variant="outlined" onClick={() => history.push(FORM_ATTRIBUTE_MAPPING_LIST_ROUTE)} style={{marginRight: 10}}>
                                    Вернуться
                                </Button>
                                {!isNew &&
                                    <Button onClick={()=>{deleteForm()}} style={{background:"#ff4343"}}>
                                        Удалить
                                    </Button>
                                }
                            </div>
                        </Toolbar>
                    </Paper>
                </Grid>
                <Grid item xs={12} spacing={1} style={{paddingTop: 16}}>
                    <Paper style={{paddingBottom: 20}}>
                        <Grid container xs={12} style={{paddingLeft: 20, paddingRight: 20}}>
                            <Grid item xs={12} style={{paddingTop: 10}}>
                                <Typography variant="h6">Источник</Typography>
                            </Grid>
                            <Grid item xs={6} style={{paddingRight: 10}}>
                                <FormControl fullWidth margin="normal">
                                    <InputLabel>Тип источника</InputLabel>
                                    <Select
                                        value={fields.sourceType || ''}
                                        onChange={(e) => { setFields({...fields, sourceType: e.target.value, sourceMember: ''}); setSourceNumber(''); }}
                                        label="Тип источника"
                                    >
                                        {formAttributeTypes.map(type => (
                                            <MenuItem key={type} value={type}>{type}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            {numberOptions.length > 0 && (
                                <Grid item xs={6} style={{paddingLeft: 10}}>
                                    <FormControl fullWidth margin="normal">
                                        <InputLabel>Номер источника</InputLabel>
                                        <Select
                                            value={sourceNumber}
                                            onChange={(e) => setSourceNumber(e.target.value)}
                                            label="Номер источника"
                                            disabled={!fields.sourceType}
                                        >
                                            <MenuItem value="">Все номера</MenuItem>
                                            {numberOptions.map(function(opt){
                                                return <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                            })}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <Autocomplete
                                    options={memberOptions}
                                    getOptionLabel={(option) => option?.label || ''}
                                    value={memberOptions.find(_ => _.value === (fields.sourceMember || '')) || null}
                                    onChange={(event, newValue) => {
                                        setFields({...fields, sourceMember: newValue ? newValue.value : ''})
                                    }}
                                    disabled={!fields.sourceType}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Поле источника" margin="normal" fullWidth />
                                    )}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} style={{paddingTop: 10}}>
                                <Typography variant="h6">Цель</Typography>
                            </Grid>
                            <Grid item xs={6} style={{paddingRight: 10}}>
                                <FormControl fullWidth margin="normal">
                                    <InputLabel>Тип цели</InputLabel>
                                    <Select
                                        value={fields.targetType || ''}
                                        onChange={(e) => {
                                            setFields({...fields, targetType: e.target.value, targetMember: '', targetNumber: ''});
                                            setTargetNumberFilter('');
                                        }}
                                        label="Тип цели"
                                    >
                                        {targetTypes.map(type => (
                                            <MenuItem key={type} value={type}>{type}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            {targetNumberOptions.length > 0 && (
                                <Grid item xs={6} style={{paddingLeft: 10}}>
                                <FormControl fullWidth margin="normal">
                                    <InputLabel>Номер цели</InputLabel>
                                    <Select
                                        value={targetNumberFilter}
                                        onChange={(e) => {
                                            setTargetNumberFilter(e.target.value);
                                            setFields({...fields, targetNumber: e.target.value});
                                        }}
                                        label="Номер цели"
                                        disabled={!fields.targetType}
                                    >
                                        <MenuItem value="">Все номера</MenuItem>
                                        {targetNumberOptions.map(function(opt){
                                            return <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                        })}
                                    </Select>
                                </FormControl>
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <Autocomplete
                                    options={targetMemberOptions}
                                    getOptionLabel={(option) => option?.label || ''}
                                    value={targetMemberOptions.find(_ => _.value === (fields.targetMember || '')) || null}
                                    onChange={(event, newValue) => {
                                        setFields({...fields, targetMember: newValue ? newValue.value : ''})
                                    }}
                                    disabled={!fields.targetType}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Поле цели" margin="normal" fullWidth />
                                    )}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField 
                                    fullWidth 
                                    margin="normal"
                                    multiline
                                    rows={4}
                                    label="Функция маппинга (опционально)"
                                    value={fields.sourceMemberMap || ''}
                                    onChange={(e) => setFields({...fields, sourceMemberMap: e.target.value})}
                                    placeholder="_ => { return _.someField; }"
                                    helperText="Введите JavaScript функцию для преобразования данных"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={<Checkbox checked={!!fields.enabled} onChange={(e) => setFields({...fields, enabled: e.target.checked})} />}
                                    label="Применять маппинг"
                                />
                            </Grid>
                            <Grid item xs={12} style={{marginTop: 20}}>
                                <LoadingButton
                                    variant="contained"
                                    onClick={submit}
                                    style={{marginRight: 10}}
                                >
                                    {isNew ? "Создать" : "Сохранить"}
                                </LoadingButton>
                                <Button
                                    variant="outlined"
                                    onClick={() => history.push(FORM_ATTRIBUTE_MAPPING_LIST_ROUTE)}
                                >
                                    Отмена
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
            </>}
        </DashboardLayout>
    )
}

export default FormAttributeMappingDetailForm 